import Button from "@/common/components/Button";
import Modal from "@/common/components/Modal";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { useAcceptNegotiation } from "../../hooks/useAcceptNegotiation";
import { SubmitHandler, useForm, useFormContext } from "react-hook-form";
import { acceptNegotiationSchema, AcceptNegotiationValidationSchema } from "../../schemas/acceptNegotiation.schema";
import { zodResolver } from "@hookform/resolvers/zod";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import { DatePicker } from "@/common/components/ui/datePicker";
import { addDays } from "date-fns";
import { getDateDiffInDays } from "@/common/utils/date";
import { formatDate } from "@/common/utils/formatters";
import { NegotiationCounterValidationSchema } from "../../schemas/negotiationCounter.schema";
import { Tooltip } from "react-tooltip";

const AcceptNegotiationModal = () => {
  const { id } = useParams();

  const queryClient = useQueryClient();

  const [messages, setMessages] = useState<string[] | boolean>(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [isSubmitModalOpen, setIsSubmitModalOpen] = useState(false);

  const { mutate: acceptedNegotiation, isError: acceptNegotiationFailed } = useAcceptNegotiation({
    negotiationId: id,
    onSuccess: () => {
      setIsSubmitModalOpen(false);
      setMessages(['Negociação aceita com sucesso!']);
      queryClient.invalidateQueries({ queryKey: ['negotiations', id] });
    },
    onError: (error) => {
      setMessages(error.response?.data.errors?.base || ['Ocorreu um erro ao aceitar a negociação']);
    },
  });

  const { watch: negotiationWatch, formState: { disabled: negotiationCounterDisabled } } = useFormContext<NegotiationCounterValidationSchema>();

  const watchNegotiationProducts = negotiationWatch('negotiation_products_attributes');
  const hasSomeCounter = watchNegotiationProducts.some((negotiationProduct) => negotiationProduct.desired_action === 'counter');
  const isEveryProductRejected = watchNegotiationProducts.every((negotiationProduct) => negotiationProduct.desired_action === 'reject');

  const { handleSubmit, watch, setValue, formState: { errors } } = useForm<AcceptNegotiationValidationSchema>({
    resolver: zodResolver(acceptNegotiationSchema),
    defaultValues: {
      due_at: addDays(new Date(), 30),
    }
  });

  const onSubmit: SubmitHandler<AcceptNegotiationValidationSchema> = (data) => acceptedNegotiation(data);

  return (
    <>
      <Modal
        isOpen={isConfirmationModalOpen}
        setIsOpen={setIsConfirmationModalOpen}
        triggerDisabled={isEveryProductRejected}
        Trigger={
          (!negotiationCounterDisabled && !hasSomeCounter) ? (
            <>
              <Button disabled={isEveryProductRejected} data-tooltip-id={isEveryProductRejected ? 'every-product-is-rejected-tooltip' : ''}>
                Aceitar negociação
              </Button>

              <Tooltip id="every-product-is-rejected-tooltip">
                Você recusou todos os produtos
              </Tooltip>
            </>
          ) : <></>
        }
        body={(
          <div className="flex flex-col gap-4">
            <p className="text-white-800 text-2xl">Aceitar negociação</p>

            <div className="flex flex-col text-white-400">
              <p>Você tem certeza que deseja aceitar essa negociação?</p>
            </div>
          </div>
        )}
        useCancellableFooter
        cancelButtonText="Voltar"
        confirmButtonText="Aceitar"
        cancelButtonClassName="bg-black-600"
        confirmButtonClassName="bg-primary-blue"
        className="rounded-lg overflow-hidden bg-black-900"
        onConfirm={() => {
          setIsConfirmationModalOpen(false);
          setIsSubmitModalOpen(true);
        }}
      />

      <Modal
        isOpen={isSubmitModalOpen}
        setIsOpen={setIsSubmitModalOpen}
        title="Você aceitou a negociação"
        subtitle="Para finalizar o processo, defina uma data de validade para negociação"
        body={(
          <div className="flex flex-col gap-1 text-white-800">
            <DatePicker
              name=""
              label="Aceitar negociação"
              markAsRequired
              date={watch('due_at')}
              setDate={(date) => (date instanceof Date && getDateDiffInDays(new Date(), date) > 0) && setValue('due_at', date)}
              formatter={(date) => `${getDateDiffInDays(new Date(), date)} dias`}
              variant="default"
              className="w-full"
              inputClassName="h-[52px] text-sm rounded-md rounded-r-none bg-neutral-op-8 hover:bg-neutral-op-8"
              errorMessage={errors.due_at?.message || undefined}
            />

            <div className="flex flex-col items-end">
              <p className="text-white-400 italic font-light text-sm">Data referente a validade</p>
              <p className="text-white-800 font-light text-sm">{formatDate(watch('due_at'))}</p>
            </div>
          </div>
        )}
        footer="Salvar"
        footerClassName="bg-primary-blue text-white-800"
        className="rounded-lg overflow-hidden"
        onConfirm={handleSubmit(onSubmit)}
      />

      {messages && (
        <ModalMessageSimple
          messages={messages}
          setMessages={setMessages}
          isError={acceptNegotiationFailed}
        />
      )}
    </>
  );
};

export default AcceptNegotiationModal;
